const componentGroups = [
  {
    selector: 'quick-add-component, quick-add-dialog',
    modules: [
      '@theme/quick-add',
      '@theme/variant-picker',
      '@theme/product-form',
      '@theme/fly-to-cart',
      '@theme/component-quantity-selector',
    ],
    interactionOnlyOnCollection: true,
  },
  {
    selector: 'product-form-component, add-to-cart-component, fly-to-cart',
    modules: ['@theme/product-form', '@theme/fly-to-cart'],
    interactionOnlyOnCollection: true,
  },
  { selector: 'product-card, product-card-link, swatches-variant-picker-component', modules: ['@theme/product-card'] },
  { selector: 'variant-picker', modules: ['@theme/variant-picker'] },
  { selector: 'accordion-custom', modules: ['@theme/accordion-custom'] },
  { selector: 'deferred-media, product-model', modules: ['@theme/media'] },
  { selector: 'product-price', modules: ['@theme/product-price'] },
  { selector: 'product-sku-component', modules: ['@theme/product-sku'] },
  { selector: 'product-inventory', modules: ['@theme/product-inventory'] },
  { selector: 'show-more-component', modules: ['@theme/show-more'] },
  { selector: 'slideshow-component', modules: ['@theme/slideshow'] },
  { selector: 'layered-slideshow-component', modules: ['@theme/layered-slideshow'] },
  { selector: 'anchored-popover-component', modules: ['@theme/anchored-popover'] },
  { selector: 'floating-panel-component', modules: ['@theme/floating-panel'] },
  { selector: 'video-background-component', modules: ['@theme/video-background'] },
  {
    selector: 'quantity-selector-component',
    modules: ['@theme/component-quantity-selector'],
    interactionOnlyOnCollection: true,
  },
  { selector: 'media-gallery', modules: ['@theme/media-gallery'] },
  { selector: 'rte-formatter', modules: ['@theme/rte-formatter'] },
  { selector: 'volume-pricing', modules: ['@theme/volume-pricing'] },
  { selector: 'price-per-item', modules: ['@theme/price-per-item'] },
  { selector: 'volume-pricing-info', modules: ['@theme/volume-pricing-info'] },
  { selector: 'cart-discount-component', modules: ['@theme/cart-discount'] },
  { selector: 'localization-form-component', modules: ['@theme/localization'] },
];
const componentSelector = componentGroups.map(({ selector }) => selector).join(',');
const isCollectionPage = window.Theme?.template?.name === 'collection';
const isProductPage = window.Theme?.template?.name === 'product';

/** @type {Map<string, Promise<unknown>>} */
const moduleRequests = new Map();
const observedElements = new WeakSet();

/**
 * AWK product pages expose a hidden native-compatible product grid only so
 * quick-add requests from other pages can parse it. It is not interactive on
 * the product page itself and must not trigger native component hydration.
 * @param {Element} element
 */
function isProductCompatibilitySource(element) {
  return isProductPage && Boolean(element.closest('.awk-quick-add-source'));
}

/**
 * Loads a module once and keeps failed requests retryable.
 * @param {string} moduleName
 */
function loadModule(moduleName) {
  const pendingRequest = moduleRequests.get(moduleName);
  if (pendingRequest) return pendingRequest;

  const request = import(moduleName).catch((error) => {
    moduleRequests.delete(moduleName);
    console.error(`[AWK] Komponente konnte nicht geladen werden: ${moduleName}`, error);
    throw error;
  });

  moduleRequests.set(moduleName, request);
  return request;
}

/** @param {{ modules: string[] }} group */
function loadGroup(group) {
  return Promise.all(group.modules.map(loadModule));
}

const intersectionObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const group = componentGroups.find(({ selector }) => entry.target.matches(selector));
            if (group) loadGroup(group);
            intersectionObserver?.unobserve(entry.target);
          }
        },
        { rootMargin: '80px 0px' }
      )
    : null;

/**
 * Registers matching elements for near-viewport loading.
 * @param {Document | DocumentFragment | Element} root
 */
function scan(root) {
  /** @type {Element[]} */
  const elements = [];

  if (root instanceof Element && root.matches(componentSelector)) elements.push(root);
  elements.push(...root.querySelectorAll(componentSelector));

  for (const element of elements) {
    if (isProductCompatibilitySource(element)) continue;

    const group = componentGroups.find(({ selector }) => element.matches(selector));
    if (!group) continue;

    // AWK recommendations become interactive as soon as their cards appear.
    // Hydrate them during insertion rather than waiting for the first tap.
    if (element.closest('.awk-cart-upsell-card')) {
      if (!observedElements.has(element)) {
        observedElements.add(element);
        loadGroup(group);
      }
      continue;
    }

    if (isCollectionPage && group.interactionOnlyOnCollection) continue;
    if (observedElements.has(element)) continue;
    observedElements.add(element);

    if (intersectionObserver) {
      intersectionObserver.observe(element);
    } else {
      loadGroup(group);
    }
  }
}

for (const eventName of ['pointerover', 'pointerdown', 'focusin']) {
  document.addEventListener(
    eventName,
    (event) => {
      if (!(event.target instanceof Element)) return;

      const component = event.target.closest(componentSelector);
      if (!component || isProductCompatibilitySource(component)) return;

      const group = componentGroups.find(({ selector }) => component.matches(selector));
      if (group) loadGroup(group);
    },
    { capture: true, passive: true }
  );
}

const scheduleInitialScan = () => {
  const run = () => scan(document);

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 700 });
  } else {
    window.requestAnimationFrame(() => window.setTimeout(run, 40));
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInitialScan, { once: true });
} else {
  scheduleInitialScan();
}

const pendingScanRoots = new Set();
let scanFrame = 0;

const scheduleScan = (root) => {
  // Coalesce nested insertions before scanning (cart/app updates add many descendants).
  for (const pendingRoot of pendingScanRoots) {
    if (pendingRoot.contains(root)) return;
    if (root.contains(pendingRoot)) pendingScanRoots.delete(pendingRoot);
  }
  pendingScanRoots.add(root);
  if (scanFrame) return;

  // Paint the interaction feedback before doing non-critical component discovery.
  scanFrame = window.requestAnimationFrame(() => window.setTimeout(() => {
    pendingScanRoots.forEach(scan);
    pendingScanRoots.clear();
    scanFrame = 0;
  }, 0));
};

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element || node instanceof DocumentFragment)) continue;

      const containsComponent =
        (node instanceof Element && node.matches(componentSelector)) ||
        node.querySelector(componentSelector);

      if (containsComponent) scheduleScan(node);
    }
  }
}).observe(document.body || document.documentElement, { childList: true, subtree: true });
